import * as React from 'react'
import { IonRow } from '@ionic/react'
import { format, parseISO } from 'date-fns'

import { useDeleteAdjustment } from '../mutations'
import { AdjustCard } from './adjust-card'
import { DeleteAdjustment } from './delete-adjustment'
import { EditAdjustment } from './edit-adjustment'
import { LogPoints } from './log-points'
import { LoyaltyButton } from './loyalty-button'
import { Reward } from './reward'
import { SixBoxCard } from './six-box-card'

import type { IonCheckboxCustomEvent } from '@ionic/core'
import type { CheckboxChangeEventDetail } from '@ionic/react'
import type {
  ApplyLoyaltyReward,
  LoyaltyData,
  RewardSet,
} from '../loyalty.schema'

type Props = {
  data: LoyaltyData
  disabled: boolean
  onApplyRewards: (
    rewards: Array<ApplyLoyaltyReward>,
    options?: { redeemEarly: boolean }
  ) => void
  onRemoveRewards?: () => void
}
const loyaltyContext = React.createContext<
  (LoyaltyData & { loyaltyRuleId?: string }) | null
>(null)

function useLoyalty() {
  const context = React.useContext(loyaltyContext)
  if (!context) {
    throw new Error(
      'useLoyalty has to be used within <loyaltyContext.Provider>'
    )
  }

  return context
}

function LoyaltyRedemptionDetail({
  data,
  disabled,
  onApplyRewards,
  onRemoveRewards,
}: Props) {
  const [selectedRewards, setSelectedRewards] = React.useState<
    Array<RewardSet>
  >([])

  const isDiscountApplied =
    data.loyaltyType !== 'manual' ? data.rewardApplied : false

  function redeemReward(
    reward: ApplyLoyaltyReward,
    options?: { redeemEarly: boolean }
  ) {
    onApplyRewards([reward], options)
    setSelectedRewards([])
  }

  function redeemSelectedRewards() {
    onApplyRewards(selectedRewards)
    setSelectedRewards([])
  }

  function redeemAllRewards() {
    const filteredRewards = data.loyaltyRules.map((lr) =>
      lr.rewards.filter((r) => r.rewardsAvailable)
    )

    onApplyRewards(filteredRewards.flatMap((reward) => reward))
    setSelectedRewards([])
  }

  function handleSelectReward(
    event: IonCheckboxCustomEvent<CheckboxChangeEventDetail<RewardSet>>
  ) {
    const { value, checked } = event.detail

    if (checked) {
      // Add checked item into checkList
      setSelectedRewards((prev) => [...prev, value])
    } else {
      // Remove unchecked item from checkList
      const filteredList = selectedRewards.filter((item) => item !== value)
      setSelectedRewards(filteredList)
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col items-center justify-center gap-3">
        <h1 className="text-ion-color-yellow text-3xl font-semibold">
          {data.client.displayName}
        </h1>
        <div className="text-center">
          <h2 className="text-3xl font-semibold">{`${data.pointsBalance} Points`}</h2>
          <p className="text-xl">
            {data.visitsUntilReward > 0
              ? `Visits Until Reward: ${data.visitsUntilReward}`
              : null}
          </p>
        </div>
        <div>
          <loyaltyContext.Provider value={data}>
            <LogPoints />
          </loyaltyContext.Provider>
        </div>
      </div>

      <div className="flex items-center justify-center gap-6">
        {onRemoveRewards ? (
          <div className="flex-0">
            <LoyaltyButton
              disabled={disabled || !isDiscountApplied}
              onClick={onRemoveRewards}
              fill="solid"
              color="secondary"
            >
              Remove Rewards
            </LoyaltyButton>
          </div>
        ) : null}
        <div className="flex-0">
          <LoyaltyButton
            disabled={
              disabled || !Boolean(selectedRewards.length) || isDiscountApplied
            }
            onClick={redeemSelectedRewards}
          >
            Redeem Selected
          </LoyaltyButton>
        </div>
        <div className="flex-0">
          <LoyaltyButton
            disabled={disabled || !data.rewardsAvailable || isDiscountApplied}
            onClick={redeemAllRewards}
          >
            Redeem All
          </LoyaltyButton>
        </div>
      </div>

      <div className="divide-y-2">
        {data.loyaltyRules.map((loyaltyRule) => (
          <React.Fragment key={loyaltyRule.loyaltyRule.objectId}>
            {loyaltyRule.rewards.map((reward, index) => (
              <div
                key={JSON.stringify(reward)}
                className="ion-padding-horizontal mx-auto max-w-lg py-6"
              >
                <Reward
                  disabled={disabled}
                  isDiscountApplied={isDiscountApplied}
                  onRedeem={redeemReward}
                  onSelect={handleSelectReward}
                  reward={reward}
                  type={data.loyaltyType}
                />
                {loyaltyRule.loyaltyRule.settings.visits > 0 ? (
                  <div className="space-y-2">
                    <loyaltyContext.Provider value={data}>
                      <SixBoxCard
                        reward={reward}
                        totalBoxes={loyaltyRule.loyaltyRule.settings.visits}
                      />
                    </loyaltyContext.Provider>
                    {index === 0 ? (
                      <IonRow className="justify-end">
                        <loyaltyContext.Provider
                          value={{
                            ...data,
                            loyaltyRuleId: reward.loyaltyRuleId,
                          }}
                        >
                          <AdjustCard />
                        </loyaltyContext.Provider>
                      </IonRow>
                    ) : null}
                    {reward.loyaltyAdjustments.length ? (
                      <>
                        <h2 className="text-xl font-semibold">Adjustments</h2>
                        <div className="space-y-5">
                          {reward.loyaltyAdjustments.map((adjustment) => (
                            <IonRow key={adjustment.objectId}>
                              <div className="flex w-full flex-col gap-y-1">
                                {/* INFO */}
                                <div>
                                  <span className="text-xl">
                                    {adjustment.value} pts.
                                  </span>
                                  {adjustment.date ? (
                                    <span>
                                      {' '}
                                      on{' '}
                                      {format(
                                        parseISO(adjustment.date),
                                        'MM/dd/yy'
                                      )}
                                    </span>
                                  ) : null}{' '}
                                  by <span>{adjustment.user.displayName}</span>
                                </div>
                                {/* BUTTONS */}
                                <div className="flex gap-x-3">
                                  {adjustment.objectId ? (
                                    <loyaltyContext.Provider value={data}>
                                      <EditAdjustment
                                        objectId={adjustment.objectId}
                                        value={adjustment.value}
                                      />
                                      <DeleteAdjustment
                                        clientId={data.client.objectId}
                                        objectId={adjustment.objectId}
                                      />
                                    </loyaltyContext.Provider>
                                  ) : null}
                                </div>
                              </div>
                            </IonRow>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
export { LoyaltyRedemptionDetail, useLoyalty }
